/**
 * All site content.
 *
 * Copy is largely from the Claude Design comp, which read well. Technical
 * claims have been checked against the actual codebases in this repo and
 * corrected where the comp invented details:
 *
 *   - "PHP / Laravel"  ->  PHP 8.3, no framework. Relay's API is hand-rolled.
 *   - "Redis"          ->  removed; nothing here uses it.
 *
 * ⚠️ Anything marked NEEDS-OWNER is unverified and must be confirmed before it
 * can be treated as fact.
 */

export const cases = [
  {
    num: '01',
    title: 'Cable operator billing platform',
    meta: 'Live on Google Play',
    body: 'Operators tracked thousands of subscribers, plan changes and monthly collections on paper ledgers. Relay puts the whole business — customers, plans, bills, payments — on the phone in the collector\'s hand.',
    tags: ['PHP 8.3', 'MySQL', 'Flutter', 'React', 'JWT', 'LiteSpeed'],
    metrics: [
      { value: '57', label: 'migrations on production' },
      { value: '6', label: 'languages supported' },
      { value: '2', label: 'clients, one API' },
    ],
  },
  {
    num: '02',
    title: 'Multi-tenant HR & payroll',
    meta: 'In production',
    body: 'Attendance lived in registers and turnstile exports that somebody reconciled by hand every month. Payroll inherited every mistake. Now each company is its own tenant on one deployment, with salary rules as data rather than code.',
    tags: ['PHP', 'MySQL', 'REST', 'Multi-tenancy', 'Linux'],
    metrics: [
      { value: 'Multi', label: 'tenant, isolated data' },
      { value: 'Live', label: 'real payroll cycles' },
    ],
  },
  {
    num: '03',
    title: 'Jewellery retail POS',
    meta: 'Deployed with retail clients',
    body: 'Jewellery pricing is not a price tag: the same item is worth something different today than yesterday, and the total depends on purity, weight, making charges and wastage. Done on a calculator at the counter it is slow, and wrong often enough to matter.',
    tags: ['PHP', 'MySQL', 'JavaScript', 'Thermal printing'],
    metrics: [
      { value: 'Daily', label: 'gold rate sync' },
      { value: 'Live', label: 'in active retail use' },
    ],
  },
];

export const offers = [
  {
    num: '01',
    title: 'It runs after you ship it',
    body: 'I have been the person paged at 2am. That changes how you build: boring deploys, logs you can actually read, and migrations that do not need a hero.',
  },
  {
    num: '02',
    title: 'Both halves of the stack',
    body: 'I write the API and then set up the box it lives on. No handoff, no "works on my machine", no waiting on someone else to open a port.',
  },
  {
    num: '03',
    title: 'Careful with live data',
    body: 'Real customers and real payments sit behind my migrations. Additive, idempotent, verified on the server afterwards — never assumed.',
  },
  {
    num: '04',
    title: 'A system the next person can run',
    body: 'Documented deploys, readable structure, credentials where they belong. Continuity, not dependence on me.',
  },
];

/**
 * ⚠️ NEEDS-OWNER: only the Wipro role and "3+ years" are confirmed. The years
 * below come from the design comp and are a plausible reconstruction. Entries
 * flagged `draft` render with a visible marker until Soorya confirms them.
 */
export const journey = [
  {
    year: '2021',
    title: 'Started in support & systems',
    body: 'Learned Linux the way it sticks: on machines other people depended on.',
    accent: 'none',
    draft: true,
  },
  {
    year: '2022',
    title: 'Linux Administrator at Wipro',
    body: 'Enterprise production servers: uptime, patching, incidents, escalations.',
    accent: 'none',
    draft: true,
  },
  {
    year: '2023 — 2024',
    title: 'First products shipped',
    body: 'Jewellery POS and the billing platform go live — my code, my servers.',
    accent: 'orange',
    draft: false,
  },
  {
    year: '2025 — now',
    title: 'Operating three systems',
    body: 'Multi-tenant SaaS, Play Store release, retail POS — all still running.',
    accent: 'ink',
    draft: false,
  },
];

export const toolkit = [
  {
    group: 'Linux & infrastructure',
    items: [
      'RHEL / CentOS / Ubuntu',
      'Nginx & Apache',
      'Bash scripting & cron',
      'SSH, firewalls, hardening',
      'SSL / domains / DNS',
      'Backups & recovery',
      'Monitoring & log triage',
    ],
  },
  {
    group: 'Backend',
    items: [
      'PHP 8',
      'REST API design',
      'MySQL & query tuning',
      'Schema migrations',
      'Auth & role-based access',
      'Multi-tenancy',
      'Payment & SMS integrations',
    ],
  },
  {
    group: 'Frontend & mobile',
    items: [
      'Flutter',
      'Play Store releases',
      'React',
      'JavaScript',
      'Responsive dashboards',
      'Thermal / POS printing',
    ],
  },
  {
    group: 'Practice',
    items: [
      'Git & release discipline',
      'Zero-downtime deploys',
      'Incident response',
      'Documentation & handover',
      'Client communication',
      'On-call ownership',
    ],
  },
];

/**
 * FAQ.
 *
 * Written for the audience this site actually has — recruiters and hiring
 * managers — rather than the freelance-services questions the reference site
 * asks ("Do you work under NDA?", "Which plan fits my project?"). Every answer
 * here is something Soorya can stand behind in an interview.
 */
export const faqs = [
  {
    q: 'Are you open to new roles?',
    a: 'Yes. I am currently a Linux Administrator at Wipro and open to conversations about infrastructure, platform or full-stack roles. The fastest way to reach me is email or WhatsApp.',
  },
  {
    q: 'What do you actually own end to end?',
    a: 'For the three systems on this page: the application code, the database schema and its migrations, the server they run on, the domain and TLS, the deploy pipeline, and whatever happens at 2am when something breaks.',
  },
  {
    q: 'Are you a sysadmin or a developer?',
    a: 'Both, and the combination is the point. I administer Linux professionally, and I build the products that run on it. That means I design systems that are operable rather than handing someone else a problem at deploy time.',
  },
  {
    q: 'Would you relocate?',
    a: 'I am based between Coimbatore and Tiruchengode, Tamil Nadu, and work IST. Open to discussing relocation or remote arrangements for the right role.',
  },
  {
    q: 'Can I see the code?',
    a: 'The billing platform is live on Google Play and its web app is public. The HR and POS systems hold client data, so I can walk through architecture and specific decisions rather than share repositories.',
  },
  {
    q: 'What are you strongest at?',
    a: 'Keeping things up. Idempotent migrations against live data, deploys that are boring by design, and debugging a production issue from a log rather than a guess.',
  },
];

/**
 * Testimonials.
 *
 * ⚠️ EMPTY BY DESIGN. The reference site has eight real client quotes; Soorya
 * has none yet. The carousel is fully built and renders the moment real quotes
 * are added here — but inventing praise on a page whose entire job is
 * credibility is the one thing that would genuinely damage him.
 *
 * Shape: { quote, name, role, org }
 */
export const testimonials = [];

export const marquee = [
  'Linux & infrastructure',
  'PHP · Flutter · MySQL',
  'Zero-downtime deploys',
  'Backups & monitoring',
  'I build it, then I keep it running',
];
